import React, { useState, useEffect } from 'react';
import { Settings, Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import { 
  getMe, 
  getAllDropdowns, 
  updateDropdownOptions, 
  addDropdownOption, 
  removeDropdownOption,
  getAllTextContent,
  updateTextContent,
  createTextContent,
  deleteTextContent
} from '../services/api';
import { toast } from 'sonner';

export default function CMSManagement() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dropdowns');
  
  // Dropdown state
  const [dropdowns, setDropdowns] = useState([]);
  const [editingDropdown, setEditingDropdown] = useState(null);
  const [newOption, setNewOption] = useState({});
  
  // Text content state
  const [textContent, setTextContent] = useState([]);
  const [editingContent, setEditingContent] = useState(null);
  const [newContent, setNewContent] = useState({
    content_key: '',
    content_value: '',
    page: 'landing',
    description: ''
  });
  const [showNewContentForm, setShowNewContentForm] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getMe();
        setUser(response.data);
      } catch (error) {
        toast.error('Authentication required');
      }
    };
    checkAuth();
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dropdownsRes, textContentRes] = await Promise.all([
        getAllDropdowns(),
        getAllTextContent()
      ]);
      setDropdowns(dropdownsRes.data);
      setTextContent(textContentRes.data);
    } catch (error) {
      console.error('Error loading CMS data:', error);
      toast.error('Failed to load CMS data');
    } finally {
      setLoading(false);
    }
  };

  // Dropdown functions
  const handleAddOption = async (fieldName) => {
    const option = newOption[fieldName];
    if (!option || !option.trim()) {
      toast.error('Please enter an option');
      return;
    }

    try {
      await addDropdownOption(fieldName, option.trim());
      toast.success('Option added');
      setNewOption({ ...newOption, [fieldName]: '' });
      loadData();
    } catch (error) {
      console.error('Error adding option:', error);
      toast.error('Failed to add option');
    }
  };

  const handleRemoveOption = async (fieldName, option) => {
    if (!window.confirm(`Remove "${option}"?`)) return;

    try {
      await removeDropdownOption(fieldName, option);
      toast.success('Option removed');
      loadData();
    } catch (error) {
      console.error('Error removing option:', error);
      toast.error('Failed to remove option');
    }
  };

  const handleSaveDropdown = async (fieldName, options) => {
    try {
      await updateDropdownOptions(fieldName, options);
      toast.success('Dropdown updated');
      setEditingDropdown(null);
      loadData();
    } catch (error) {
      console.error('Error updating dropdown:', error);
      toast.error('Failed to update dropdown');
    }
  };

  // Text content functions
  const handleUpdateContent = async (contentKey, contentValue) => {
    try {
      const content = textContent.find(c => c.content_key === contentKey);
      await updateTextContent(contentKey, {
        content_key: contentKey,
        content_value: contentValue,
        page: content.page,
        description: content.description
      });
      toast.success('Content updated');
      setEditingContent(null);
      loadData();
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error('Failed to update content');
    }
  };

  const handleCreateContent = async () => {
    if (!newContent.content_key || !newContent.content_value) {
      toast.error('Please fill in content key and value');
      return;
    }

    try {
      await createTextContent(newContent);
      toast.success('Content created');
      setNewContent({ content_key: '', content_value: '', page: 'landing', description: '' });
      setShowNewContentForm(false);
      loadData();
    } catch (error) {
      console.error('Error creating content:', error);
      toast.error('Failed to create content');
    }
  };

  const handleDeleteContent = async (contentKey) => {
    if (!window.confirm('Delete this content?')) return;

    try {
      await deleteTextContent(contentKey);
      toast.success('Content deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={() => setUser(null)} />
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Settings className="h-8 w-8 text-[#0F2A44]" />
          <div>
            <h1 className="font-manrope font-bold text-3xl text-[#0F2A44]">
              Content Management System
            </h1>
            <p className="font-public text-[#4B5563]">
              Manage dropdown options and frontend text content
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('dropdowns')}
            className={`px-6 py-3 font-manrope font-semibold transition-colors border-b-2 ${
              activeTab === 'dropdowns'
                ? 'text-[#0F2A44] border-[#0F2A44]'
                : 'text-[#4B5563] border-transparent hover:text-[#0F2A44]'
            }`}
            data-testid="cms-dropdowns-tab"
          >
            Dropdown Fields
          </button>
          <button
            onClick={() => setActiveTab('text')}
            className={`px-6 py-3 font-manrope font-semibold transition-colors border-b-2 ${
              activeTab === 'text'
                ? 'text-[#0F2A44] border-[#0F2A44]'
                : 'text-[#4B5563] border-transparent hover:text-[#0F2A44]'
            }`}
            data-testid="cms-text-tab"
          >
            Text Content
          </button>
        </div>

        {/* Dropdowns Tab */}
        {activeTab === 'dropdowns' && (
          <div className="space-y-6">
            {dropdowns.map((dropdown) => (
              <div key={dropdown.field_name} className="bg-white rounded-xl border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-manrope font-semibold text-xl text-[#0F2A44] capitalize">
                    {dropdown.field_name.replace('_', ' ')}
                  </h2>
                  <span className="text-sm text-[#4B5563] font-public">
                    {dropdown.options.length} options
                  </span>
                </div>

                {/* Add new option */}
                <div className="flex gap-3 mb-4">
                  <Input
                    value={newOption[dropdown.field_name] || ''}
                    onChange={(e) => setNewOption({ ...newOption, [dropdown.field_name]: e.target.value })}
                    placeholder="Add new option"
                    data-testid={`add-option-input-${dropdown.field_name}`}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddOption(dropdown.field_name);
                      }
                    }}
                    className="flex-1 h-10"
                  />
                  <Button
                    onClick={() => handleAddOption(dropdown.field_name)}
                    data-testid={`add-option-btn-${dropdown.field_name}`}
                    className="bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44]"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>

                {/* Options list */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {dropdown.options.map((option, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <span className="font-public text-sm text-[#262626]">{option}</span>
                      <button
                        onClick={() => handleRemoveOption(dropdown.field_name, option)}
                        data-testid={`remove-option-${dropdown.field_name}-${index}`}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Text Content Tab */}
        {activeTab === 'text' && (
          <div className="space-y-6">
            {/* Add new content button */}
            <div className="flex justify-end">
              <Button
                onClick={() => setShowNewContentForm(!showNewContentForm)}
                data-testid="add-content-btn"
                className="bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Content
              </Button>
            </div>

            {/* New content form */}
            {showNewContentForm && (
              <div className="bg-white rounded-xl border border-slate-100 p-6">
                <h3 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-4">
                  Create New Text Content
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#262626] mb-2">Content Key</label>
                    <Input
                      value={newContent.content_key}
                      onChange={(e) => setNewContent({ ...newContent, content_key: e.target.value })}
                      placeholder="e.g., landing_hero_title"
                      data-testid="new-content-key-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#262626] mb-2">Content Value</label>
                    <Textarea
                      value={newContent.content_value}
                      onChange={(e) => setNewContent({ ...newContent, content_value: e.target.value })}
                      placeholder="Enter the text content"
                      data-testid="new-content-value-input"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#262626] mb-2">Page</label>
                      <Input
                        value={newContent.page}
                        onChange={(e) => setNewContent({ ...newContent, page: e.target.value })}
                        placeholder="e.g., landing, directory"
                        data-testid="new-content-page-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#262626] mb-2">Description</label>
                      <Input
                        value={newContent.description}
                        onChange={(e) => setNewContent({ ...newContent, description: e.target.value })}
                        placeholder="Brief description"
                        data-testid="new-content-description-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleCreateContent}
                      data-testid="save-new-content-btn"
                      className="bg-[#0F2A44] hover:bg-[#0F2A44]/90 text-white"
                    >
                      Create
                    </Button>
                    <Button
                      onClick={() => setShowNewContentForm(false)}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Content list */}
            {textContent.map((content) => (
              <div key={content.content_key} className="bg-white rounded-xl border border-slate-100 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-manrope font-semibold text-lg text-[#0F2A44]">
                        {content.content_key}
                      </h3>
                      <span className="px-2 py-1 bg-[#4A90E2]/10 text-[#4A90E2] text-xs rounded-full">
                        {content.page}
                      </span>
                    </div>
                    {content.description && (
                      <p className="text-sm text-[#4B5563] font-public mb-3">{content.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {editingContent === content.content_key ? (
                      <>
                        <Button
                          onClick={() => {
                            const textarea = document.getElementById(`content-${content.content_key}`);
                            handleUpdateContent(content.content_key, textarea.value);
                          }}
                          data-testid={`save-content-${content.content_key}`}
                          size="sm"
                          className="bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44]"
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => setEditingContent(null)}
                          size="sm"
                          variant="outline"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={() => setEditingContent(content.content_key)}
                          data-testid={`edit-content-${content.content_key}`}
                          size="sm"
                          variant="ghost"
                          className="text-[#4A90E2]"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteContent(content.content_key)}
                          data-testid={`delete-content-${content.content_key}`}
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {editingContent === content.content_key ? (
                  <Textarea
                    id={`content-${content.content_key}`}
                    defaultValue={content.content_value}
                    rows={4}
                    className="w-full font-public"
                  />
                ) : (
                  <p className="font-public text-[#262626] bg-slate-50 p-4 rounded-lg border border-slate-200">
                    {content.content_value}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
